import { useEffect, useRef } from "react";

/**
 * Every trap currently mounted, innermost last. Nested modals must not each
 * fight over Tab or clobber each other's scroll-lock restore.
 */
const stack: HTMLElement[] = [];

/**
 * Body scroll stays locked while any trap is mounted: captured when the first
 * one goes up, restored when the last one comes down.
 *
 * Ownership has to be read from the live stack, never captured at mount. A
 * parent modal can unmount before a dialog it opened — closing itself on
 * confirm, or a route change tearing the overlay down — and if the parent had
 * claimed the lock at mount it would restore `overflow` on the way out while
 * the nested dialog, which never claimed it, is still open and trapped.
 */
let lockedOverflow: string | null = null;

const FOCUSABLE = [
  "a[href]",
  "button:not([disabled])",
  "input:not([disabled]):not([type=hidden])",
  "select:not([disabled])",
  "textarea:not([disabled])",
  '[tabindex]:not([tabindex="-1"])',
].join(",");

/**
 * Modal focus management, per the DS Modal contract: trap Tab inside the
 * dialog, lock background scroll, and return focus to whatever opened it.
 *
 * Esc is deliberately left to the caller — only the topmost layer should close,
 * and this hook cannot know what is stacked above it.
 *
 *   const ref = useFocusTrap<HTMLDivElement>(isOpen);
 *   return isOpen ? <div ref={ref} role="dialog" aria-modal="true">…</div> : null;
 */
export function useFocusTrap<T extends HTMLElement>(active: boolean) {
  const ref = useRef<T>(null);

  useEffect(() => {
    if (!active) return;
    const node = ref.current;
    if (!node) return;

    const previouslyFocused = document.activeElement as HTMLElement | null;
    stack.push(node);

    if (stack.length === 1) {
      lockedOverflow = document.body.style.overflow;
      document.body.style.overflow = "hidden";
    }

    // Move focus in if it is not already inside.
    if (!node.contains(document.activeElement)) {
      const first = node.querySelector<HTMLElement>(FOCUSABLE);
      (first ?? node).focus();
    }

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== "Tab") return;
      // Only the innermost trap handles Tab.
      if (stack[stack.length - 1] !== node) return;
      const items = Array.from(node.querySelectorAll<HTMLElement>(FOCUSABLE)).filter(
        (el) => el.offsetParent !== null || el === document.activeElement,
      );
      // Nothing focusable inside — let Tab out. Swallowing it here would be a
      // keyboard trap (WCAG 2.1.2), which is worse than no trap at all.
      if (items.length === 0) return;
      const first = items[0];
      const last = items[items.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };

    document.addEventListener("keydown", onKeyDown, true);
    return () => {
      document.removeEventListener("keydown", onKeyDown, true);

      // Innermost is also read live: if something is still stacked above us,
      // focus belongs to it and pulling it back to our opener would land the
      // user outside a dialog that is still trapped.
      const innermost = stack[stack.length - 1] === node;

      const i = stack.indexOf(node);
      if (i !== -1) stack.splice(i, 1);

      if (stack.length === 0 && lockedOverflow !== null) {
        document.body.style.overflow = lockedOverflow;
        lockedOverflow = null;
      }

      if (innermost) previouslyFocused?.focus?.();
    };
  }, [active]);

  return ref;
}
